/* client/src/components/Reports.tsx */
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Chip,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  Button,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon
} from '@mui/icons-material';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { apiGetDashboard, apiGetFinancialReports } from '../services/apiService';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

interface BusinessUnit {
  id: number;
  name: string;
  share_percentage: number;
  profit: number;
  your_share: number;
  revenue: number;
  expenses: number;
  net_profit: number;
  profit_margin: number;
  growth_rate: number;
}

interface Transaction {
  id: number;
  date: string;
  business_unit_name: string;
  material_name: string;
  type: string;
  quantity: number;
  total_amount: number;
  unit_price: number;
}

interface FinancialMetrics {
  revenue: number;
  cogs: number;
  gross_profit: number;
  operating_expenses: number;
  net_profit: number;
  total_assets: number;
  total_liabilities: number;
  equity: number;
  cash_flow_operations: number;
  cash_flow_investing: number;
  cash_flow_financing: number;
  yoy_growth: number;
  profit_margin: number;
  current_ratio: number;
  quick_ratio: number;
}

interface DashboardData {
  partner: {
    name: string;
    email: string;
  };
  business_units: BusinessUnit[];
  total_profit: number;
  recent_transactions: Transaction[];
  financial_metrics: FinancialMetrics;
  summary?: {
    total_transactions: number;
    total_purchases: number;
    total_sales: number;
    total_expenses: number;
    net_profit: number;
  };
}

function Reports() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUnit, setSelectedUnit] = useState<string>('all');
  const [tabValue, setTabValue] = useState(0);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
    period: 'month' // month, quarter, year, custom
  });

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const dashboardData = await apiGetFinancialReports(dateRange);
      setData(dashboardData);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(20);
    doc.text('Financial Report', 14, 15);
    doc.setFontSize(12);
    doc.text(`Period: ${new Date(dateRange.start).toLocaleDateString()} - ${new Date(dateRange.end).toLocaleDateString()}`, 14, 22);
    
    // Summary Table
    if (data && data.summary) {
      autoTable(doc, {
        startY: 30,
        head: [['Metric', 'Value']],
        body: [
          ['Total Revenue', `د.${data.financial_metrics.revenue.toFixed(2)}`],
          ['Total Expenses', `د.${data.summary.total_expenses.toFixed(2)}`],
          ['Net Profit', `د.${data.financial_metrics.net_profit.toFixed(2)}`],
          ['Profit Margin', `${data.financial_metrics.profit_margin.toFixed(2)}%`],
          ['YoY Growth', `${data.financial_metrics.yoy_growth.toFixed(2)}%`]
        ]
      });
    }
    
    // Business Units Table
    if (data && data.business_units.length > 0) {
      autoTable(doc, {
        startY: (doc as any).lastAutoTable.finalY + 20,
        head: [['Business Unit', 'Revenue', 'Expenses', 'Net Profit', 'Profit Margin', 'Your Share']],
        body: data.business_units.map(unit => [
          unit.name,
          `د.${unit.revenue.toFixed(2)}`,
          `د.${unit.expenses.toFixed(2)}`,
          `د.${unit.net_profit.toFixed(2)}`,
          `${unit.profit_margin.toFixed(2)}%`,
          `د.${unit.your_share.toFixed(2)}`
        ])
      });
    }
    
    doc.save('financial-report.pdf');
  };

  const exportToExcel = () => {
    if (!data) return;
    
    const workbook = XLSX.utils.book_new();
    
    // Summary Sheet
    const summaryData = [
      ['Financial Summary', ''],
      ['Period', `${new Date(dateRange.start).toLocaleDateString()} - ${new Date(dateRange.end).toLocaleDateString()}`],
      [''],
      ['Metric', 'Value'],
      ['Total Revenue', data.financial_metrics.revenue],
      ['Total Expenses', data.summary?.total_expenses || 0],
      ['Net Profit', data.financial_metrics.net_profit],
      ['Profit Margin', data.financial_metrics.profit_margin],
      ['YoY Growth', data.financial_metrics.yoy_growth],
      ['Total Assets', data.financial_metrics.total_assets],
      ['Total Liabilities', data.financial_metrics.total_liabilities],
      ['Equity', data.financial_metrics.equity]
    ];
    
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
    
    // Business Units Sheet - Fix: Use proper type annotation or ensure array accepts mixed types
    const unitsData: any[] = [
      ['Business Unit', 'Revenue', 'Expenses', 'Net Profit', 'Profit Margin', 'Your Share', 'Growth Rate']
    ];
    
    data.business_units.forEach(unit => {
      unitsData.push([
        unit.name,
        unit.revenue,
        unit.expenses,
        unit.net_profit,
        unit.profit_margin,
        unit.your_share,
        unit.growth_rate
      ]);
    });
    
    const unitsSheet = XLSX.utils.aoa_to_sheet(unitsData);
    XLSX.utils.book_append_sheet(workbook, unitsSheet, 'Business Units');
    
    // Transactions Sheet - Fix: Use proper type annotation
    const transactionsData: any[] = [
      ['Date', 'Business Unit', 'Material', 'Type', 'Quantity', 'Unit Price', 'Total Amount']
    ];
    
    data.recent_transactions.forEach(transaction => {
      transactionsData.push([
        new Date(transaction.date).toLocaleDateString(),
        transaction.business_unit_name,
        transaction.material_name,
        transaction.type,
        transaction.quantity,
        transaction.unit_price,
        transaction.total_amount
      ]);
    });
    
    const transactionsSheet = XLSX.utils.aoa_to_sheet(transactionsData);
    XLSX.utils.book_append_sheet(workbook, transactionsSheet, 'Transactions');
    
    XLSX.writeFile(workbook, 'financial-report.xlsx');
  };

  const filteredBusinessUnits = selectedUnit === 'all' 
    ? data?.business_units || []
    : data?.business_units.filter(unit => unit.id.toString() === selectedUnit) || [];

  if (loading) return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
      <CircularProgress />
    </Container>
  );
  
  if (error) return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Alert severity="error">{error}</Alert>
    </Container>
  );
  
  if (!data) return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Alert severity="info">No data available</Alert>
    </Container>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Financial Reports
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PdfIcon />}
            onClick={exportToPDF}
            disabled={!data}
          >
            Export PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<ExcelIcon />}
            onClick={exportToExcel}
            disabled={!data}
          >
            Export Excel
          </Button>
        </Box>
      </Box>

      {/* Date Range Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Report Period
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Period</InputLabel>
              <Select
                value={dateRange.period}
                label="Period"
                onChange={(e) => {
                  const period = e.target.value;
                  let startDate = new Date();
                  
                  if (period === 'month') {
                    startDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
                  } else if (period === 'quarter') {
                    const quarter = Math.floor(startDate.getMonth() / 3);
                    startDate = new Date(startDate.getFullYear(), quarter * 3, 1);
                  } else if (period === 'year') {
                    startDate = new Date(startDate.getFullYear(), 0, 1);
                  }
                  
                  setDateRange({
                    ...dateRange,
                    period,
                    start: startDate.toISOString().split('T')[0]
                  });
                }}
              >
                <MenuItem value="month">This Month</MenuItem>
                <MenuItem value="quarter">This Quarter</MenuItem>
                <MenuItem value="year">This Year</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {dateRange.period === 'custom' && (
            <>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}
          
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              label="Filter by Business Unit"
              value={selectedUnit}
              onChange={(e) => setSelectedUnit(e.target.value)}
            >
              <MenuItem value="all">All Business Units</MenuItem>
              {data.business_units.map((unit) => (
                <MenuItem key={unit.id} value={unit.id.toString()}>
                  {unit.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab label="Summary" />
        <Tab label="Business Units" />
        <Tab label="Financial Statements" />
        <Tab label="Transactions" />
      </Tabs>

      {tabValue === 0 && (
        <Grid container spacing={3}>
          {/* Key Financial Metrics */}
          <Grid item xs={12}>
            <Typography variant="h5" gutterBottom>
              Key Financial Metrics
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" component="div" color="primary">
                  د.{data.financial_metrics.revenue.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Revenue
                </Typography>
                <Typography variant="caption" color={data.financial_metrics.yoy_growth >= 0 ? "success.main" : "error.main"}>
                  {data.financial_metrics.yoy_growth >= 0 ? '↑' : '↓'} {Math.abs(data.financial_metrics.yoy_growth)}% YoY
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" component="div" color="error.main">
                  د.{(data.summary?.total_expenses || 0).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Expenses
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" component="div" color="success.main">
                  د.{data.financial_metrics.net_profit.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Net Profit
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" component="div" color="info.main">
                  {data.financial_metrics.profit_margin.toFixed(2)}%
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Profit Margin
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Financial Health Indicators */}
          <Grid item xs={12}>
            <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
              Financial Health
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" component="div" color={data.financial_metrics.current_ratio >= 1.5 ? "success.main" : "warning.main"}>
                  {data.financial_metrics.current_ratio.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Current Ratio
                </Typography>
                <Typography variant="caption">
                  {data.financial_metrics.current_ratio >= 1.5 ? 'Healthy' : 'Needs Attention'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" component="div" color={data.financial_metrics.quick_ratio >= 1 ? "success.main" : "warning.main"}>
                  {data.financial_metrics.quick_ratio.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Quick Ratio
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" component="div" color={data.financial_metrics.equity > 0 ? "success.main" : "error.main"}>
                  د.{data.financial_metrics.equity.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Owner's Equity
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Profit Distribution Chart */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Profit Distribution
                </Typography>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
  <PieChart>
    <Pie
      data={[
        { name: 'Net Profit', value: data.financial_metrics.net_profit },
        { name: 'Expenses', value: data.summary?.total_expenses || 0 },
        { name: 'COGS', value: data.financial_metrics.cogs },
      ]}
      cx="50%"
      cy="50%"
      outerRadius={80}
      fill="#8884d8"
      dataKey="value"
      label={({ name, value, percent }) => 
        `${name}: ${((percent as number) * 100).toFixed(0)}%`
      }
    >
      <Cell fill="#0088FE" />
      <Cell fill="#00C49F" />
      <Cell fill="#FFBB28" />
    </Pie>
    <Tooltip formatter={(value) => [`د.${Number(value).toLocaleString()}`, 'Value']} />
  </PieChart>
</ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Revenue Trend Chart */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Revenue Trend
                </Typography>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
  <BarChart
    data={[
      { month: 'Jan', revenue: 400000 },
      { month: 'Feb', revenue: 450000 },
      { month: 'Mar', revenue: 520000 },
      { month: 'Apr', revenue: 480000 },
      { month: 'May', revenue: 550000 },
      { month: 'Jun', revenue: 600000 },
    ]}
    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
  >
    <CartesianGrid strokeDasharray="3 3" />
    <XAxis dataKey="month" />
    <YAxis tickFormatter={(value) => `د.${(value / 1000).toFixed(0)}K`} />
    <Tooltip formatter={(value) => [`د.${value.toLocaleString()}`, 'Revenue']} />
    <Legend />
    <Bar dataKey="revenue" fill="#8884d8" name="Revenue" />
  </BarChart>
</ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {tabValue === 1 && (
        <Grid container spacing={3}>
          {filteredBusinessUnits.map(unit => (
            <Grid item xs={12} key={unit.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      {unit.name} - Performance Report
                    </Typography>
                    <Chip 
                      label={unit.net_profit >= 0 ? "Profitable" : "Loss"} 
                      color={unit.net_profit >= 0 ? "success" : "error"} 
                    />
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="textSecondary">
                          Revenue
                        </Typography>
                        <Typography variant="h6" color="primary">
                          د.{unit.revenue.toLocaleString()}
                        </Typography>
                      </Paper>
                    </Grid>
                    
                    <Grid item xs={12} md={3}>
                      <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="textSecondary">
                          Expenses
                        </Typography>
                        <Typography variant="h6" color="error.main">
                          د.{unit.expenses.toLocaleString()}
                        </Typography>
                      </Paper>
                    </Grid>
                    
                    <Grid item xs={12} md={3}>
                      <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="textSecondary">
                          Net Profit
                        </Typography>
                        <Typography variant="h6" color={unit.net_profit >= 0 ? "success.main" : "error.main"}>
                          د.{unit.net_profit.toLocaleString()}
                        </Typography>
                      </Paper>
                    </Grid>
                    
                    <Grid item xs={12} md={3}>
                      <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="textSecondary">
                          Profit Margin
                        </Typography>
                        <Typography variant="h6" color={unit.profit_margin >= 20 ? "success.main" : "warning.main"}>
                          {unit.profit_margin.toFixed(2)}%
                        </Typography>
                      </Paper>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="textSecondary">
                          Your Share Percentage
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {unit.share_percentage}%
                        </Typography>
                      </Paper>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="textSecondary">
                          Your Share Value
                        </Typography>
                        <Typography variant="h6" color={unit.your_share >= 0 ? "success.main" : "error.main"}>
                          د.{unit.your_share.toLocaleString()}
                        </Typography>
                      </Paper>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="textSecondary">
                          Growth Rate
                        </Typography>
                        <Typography variant="h6" color={unit.growth_rate >= 0 ? "success.main" : "error.main"}>
                          {unit.growth_rate >= 0 ? '+' : ''}{unit.growth_rate.toFixed(2)}%
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {tabValue === 2 && (
        <Grid container spacing={3}>
          {/* Income Statement */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Income Statement
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Revenue</strong></TableCell>
                        <TableCell align="right">د.{data.financial_metrics.revenue.toLocaleString()}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Cost of Goods Sold</TableCell>
                        <TableCell align="right">د.{data.financial_metrics.cogs.toLocaleString()}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Gross Profit</strong></TableCell>
                        <TableCell align="right">د.{data.financial_metrics.gross_profit.toLocaleString()}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Operating Expenses</TableCell>
                        <TableCell align="right">د.{data.financial_metrics.operating_expenses.toLocaleString()}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Net Profit</strong></TableCell>
                        <TableCell align="right">د.{data.financial_metrics.net_profit.toLocaleString()}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Balance Sheet */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Balance Sheet
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={2}><strong>Assets</strong></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Total Assets</TableCell>
                        <TableCell align="right">د.{data.financial_metrics.total_assets.toLocaleString()}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={2}><strong>Liabilities</strong></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Total Liabilities</TableCell>
                        <TableCell align="right">د.{data.financial_metrics.total_liabilities.toLocaleString()}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={2}><strong>Equity</strong></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Owner's Equity</TableCell>
                        <TableCell align="right">د.{data.financial_metrics.equity.toLocaleString()}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Cash Flow Statement */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Cash Flow Statement
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>Cash from Operations</TableCell>
                        <TableCell align="right">د.{data.financial_metrics.cash_flow_operations.toLocaleString()}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Cash from Investing</TableCell>
                        <TableCell align="right">د.{data.financial_metrics.cash_flow_investing.toLocaleString()}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Cash from Financing</TableCell>
                        <TableCell align="right">د.{data.financial_metrics.cash_flow_financing.toLocaleString()}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Net Cash Flow</strong></TableCell>
                        <TableCell align="right">
                          د.{(data.financial_metrics.cash_flow_operations + 
                              data.financial_metrics.cash_flow_investing + 
                              data.financial_metrics.cash_flow_financing).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {tabValue === 3 && (
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom>
            Recent Transactions
          </Typography>
          {data.recent_transactions.length === 0 ? (
            <Alert severity="info">No recent transactions</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Business Unit</TableCell>
                    <TableCell>Material</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.recent_transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>{new Date(transaction.date).toLocaleDateString()}</TableCell>
                      <TableCell>{transaction.business_unit_name}</TableCell>
                      <TableCell>{transaction.material_name}</TableCell>
                      <TableCell>
                        <Chip 
                          label={transaction.type} 
                          color={transaction.type === 'sale' ? "success" : "primary"} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell align="right">{transaction.quantity}</TableCell>
                      <TableCell align="right">د.{transaction.unit_price.toFixed(2)}</TableCell>
                      <TableCell align="right">
                        <Typography 
                          color={transaction.type === 'sale' ? "success.main" : "primary.main"}
                          fontWeight="bold"
                        >
                          د.{transaction.total_amount.toFixed(2)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Grid>
      )}
    </Container>
  );
}

export default Reports;